/**
 * Outfame Button Component
 *
 * Design system button with multiple variants and sizes.
 * Renders as Link when href is provided, otherwise as button.
 */
import Link from "next/link"
import { ReactNode, ButtonHTMLAttributes } from "react"

export type ButtonVariant = "primary" | "secondary" | "ghost" | "outline" | "outline-solid" | "pink" | "light"
export type ButtonSize = "sm" | "md" | "lg"
export type ButtonContext = "light" | "dark"

interface ButtonBaseProps {
    variant?: ButtonVariant
    size?: ButtonSize
    context?: ButtonContext
    children: ReactNode
    className?: string
    icon?: ReactNode
    iconPosition?: "left" | "right"
}

interface ButtonAsLink extends ButtonBaseProps {
    href: string
    type?: never
    onClick?: never
    disabled?: never
}

interface ButtonAsButton extends ButtonBaseProps {
    href?: never
    type?: "button" | "submit" | "reset"
    onClick?: () => void
    disabled?: boolean
}

type ButtonProps = ButtonAsLink | ButtonAsButton

/**
 * Get Tailwind classes for button variant + context combination
 */
function getVariantClasses(variant: ButtonVariant, context: ButtonContext): string {
    const variants: Record<ButtonVariant, Record<ButtonContext, string>> = {
        primary: {
            light: "bg-black text-white hover:opacity-90 active:opacity-80",
            dark: "bg-white text-black hover:opacity-90 active:opacity-80"
        },
        secondary: {
            light: "bg-white text-black border border-black/20 hover:border-black/40 hover:bg-cream",
            dark: "bg-white/12 text-white hover:bg-white/20 active:bg-white/25"
        },
        ghost: {
            light: "bg-transparent text-black border border-black/20 hover:bg-cream active:bg-sand-light",
            dark: "bg-transparent text-white border border-white/20 hover:bg-white/10 active:bg-white/20"
        },
        outline: {
            light: "bg-transparent text-black border border-black/20 hover:border-black/40 hover:bg-cream",
            dark: "bg-transparent text-white border border-white/20 hover:border-white/40 hover:bg-white/10"
        },
        "outline-solid": {
            light: "bg-transparent text-black border border-black hover:bg-cream",
            dark: "bg-transparent text-white border border-white hover:bg-white/10"
        },
        pink: {
            light: "bg-pink text-black hover:bg-pink-dark active:opacity-80",
            dark: "bg-pink text-black hover:bg-pink-dark active:opacity-80"
        },
        light: {
            light: "bg-[#e8e8e4] text-black hover:bg-[#dcdcd8] active:bg-[#d0d0cc]",
            dark: "bg-[#e8e8e4] text-black hover:bg-[#dcdcd8] active:bg-[#d0d0cc]"
        }
    }

    return variants[variant][context]
}

/**
 * Get Tailwind classes for button size
 *
 * CONVERSION-OPTIMIZED MOBILE-FIRST SIZING:
 * Research findings (90 high-converting landing pages):
 * - Average CTA height: 51.76px
 * - Optimal mobile range: 52-60px for conversion CTAs
 * - 15%+ CTR improvement with larger buttons
 *
 * Size Strategy:
 * - sm: Utility buttons (44px mobile → 36px desktop)
 * - md: Standard buttons (48px mobile → 44px desktop)
 * - lg: CONVERSION-CRITICAL CTAs (56px mobile → 48px desktop)
 */
function getSizeClasses(size: ButtonSize): string {
    const sizes: Record<ButtonSize, string> = {
        // Mobile-first: optimized for touch + conversion
        sm: "h-11 md:h-9 px-4 md:px-3 text-[14px] active:scale-[0.97]", // 44px → 36px (utility)
        md: "h-12 md:h-11 px-5 md:px-4 text-[16px] active:scale-[0.97]", // 48px → 44px (standard)
        lg: "h-14 md:h-12 px-6 md:px-5 text-[16px] active:scale-[0.98]" // 56px → 48px (conversion-critical, often full width)
    }

    return sizes[size]
}

/**
 * Optical alignment: an icon reads lighter than text, so the icon side gets 2px less
 * padding than the text side (sizes above: 16/12, 20/16, 24/20px).
 */
function getIconSidePadding(size: ButtonSize, iconPosition: "left" | "right"): string {
    const padding: Record<"left" | "right", Record<ButtonSize, string>> = {
        left: { sm: "ps-[14px] md:ps-[10px]", md: "ps-[18px] md:ps-[14px]", lg: "ps-[22px] md:ps-[18px]" },
        right: { sm: "pe-[14px] md:pe-[10px]", md: "pe-[18px] md:pe-[14px]", lg: "pe-[22px] md:pe-[18px]" }
    }

    return padding[iconPosition][size]
}

export default function Button({ variant = "primary", size = "md", context = "light", children, className = "", icon, iconPosition = "right", ...props }: ButtonProps) {
    // Press feedback: scale per size (0.97 small, 0.98 large), transform eased 150ms with the colour props
    const baseClasses = "inline-flex items-center justify-center font-semibold rounded-button transition-[color,background-color,border-color,opacity,transform,scale] duration-150 ease-out motion-reduce:transition-none disabled:active:scale-100"
    const variantClasses = getVariantClasses(variant, context)
    const sizeClasses = getSizeClasses(size)
    const iconPaddingClasses = icon ? getIconSidePadding(size, iconPosition) : ""

    const combinedClasses = `${baseClasses} ${variantClasses} ${sizeClasses} ${iconPaddingClasses} ${className}`

    const content = (
        <>
            {icon && iconPosition === "left" && <span className="mr-2">{icon}</span>}
            <span>{children}</span>
            {icon && iconPosition === "right" && <span className="ml-2">{icon}</span>}
        </>
    )

    // Render as Link if href is provided
    if ("href" in props && props.href) {
        return (
            <Link href={props.href} className={combinedClasses}>
                {content}
            </Link>
        )
    }

    // Render as button otherwise
    const buttonProps = props as ButtonAsButton
    return (
        <button type={buttonProps.type || "button"} onClick={buttonProps.onClick} disabled={buttonProps.disabled} className={`${combinedClasses} ${buttonProps.disabled ? "cursor-not-allowed opacity-50" : ""}`}>
            {content}
        </button>
    )
}
